import {defaultLevelConfig} from "../../config";
import {finalize} from "../core/FloatUtils.js";
import {Vec2} from "../core/Vec2.js";

/**
 * Checks if a given point is inside an ellipse (expanded by a margin).
 *
 * @param point - The point to test (e.g. player's position).
 * @param center - The center of the ellipse.
 * @param rotation - The ellipse’s rotation (in radians).
 * @param radii - An object with rx and ry which represent the half-extents of the ellipse.
 * @param margin - An additional margin (defaulting to defaultLevelConfig.playerRadius).
 * @returns True if the point is inside the ellipse expanded by the margin, false otherwise.
 */
export function isPointInsideEllipse(
  point: Vec2,
  center: Vec2,
  rotation: number,
  radii: {rx: number; ry: number},
  margin: number = defaultLevelConfig.playerRadius,
): boolean {
  // Compute the difference vector between the point and the center.
  const dx = point.x - center.x;
  const dy = point.y - center.y;

  // Rotate the difference vector by -rotation so the ellipse becomes axis-aligned.
  const cosA = Math.cos(-rotation);
  const sinA = Math.sin(-rotation);
  const xPrime = dx * cosA - dy * sinA;
  const yPrime = dx * sinA + dy * cosA;

  // Expand the radii by the margin.
  const effectiveRx = radii.rx + margin;
  const effectiveRy = radii.ry + margin;

  // Compute the ellipse check value.
  const ellipseValue = finalize(
    (xPrime * xPrime) / (effectiveRx * effectiveRx) +
      (yPrime * yPrime) / (effectiveRy * effectiveRy),
  );
  // If the value is less than or equal to 1, then the point is inside the ellipse.
  return ellipseValue <= 1;
}
